import React, {useContext, useEffect, useState} from 'react';
import {
    Box, Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl, FormControlLabel,
    InputLabel,
    MenuItem,
    OutlinedInput,
    Select,
    useMediaQuery,
    useTheme
} from '@material-ui/core';
import {Button, Typography} from '../../../../common/components/libs';
import DocumentsStoreContext from '../../context';
import {createServiceOrder} from '../../../../services/organizationBranches';
import {CardServiceOrder, InvoiceServiceOrder} from '../../../../services/organizationBranches/models';
import {changeEventHandlerDecorator} from '../../../../common/libs';
import {useStores} from '../../../../hooks';
import {TariffPeriod, TariffVariant} from '../../models';
import {SnackbarType} from '../../../../stores/common';

const prices = {
    Simple: 990,
    Basic: 2900,
    Advanced: 3900
}

const months = {
    Month: 1,
    HalfYear: 6,
    Year: 12
}

const discounts = {
    Month: 0,
    HalfYear: 10,
    Year: 25
}

interface TariffPaymentFormProps {
    open: boolean,
    tariff: TariffVariant,
    onClose: () => void
}

export const TariffPaymentForm: React.FC<TariffPaymentFormProps> = ({open, tariff, onClose}) => {
    const {constantsStore, generalStore} = useStores()
    const [chosenPeriod, setChosenPeriod] = useState<TariffPeriod>('Year')
    const [chosenTariff, setChosenTariff] = useState<TariffVariant>(tariff)
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const currencyFormatter = constantsStore.getCurrencyFormatter('RUB');
    const [isDocsChecked, setIsDocsChecked] = useState<boolean>(true)

    const theme = useTheme();
    const isSm = useMediaQuery(theme.breakpoints.up('sm'));

    const documentsStore = useContext(DocumentsStoreContext);

    const {isServiceLegalDetailsLoaded, serviceLegalDetails} = documentsStore;

    const calculatePrice = () => {
        if (!open) return

        const basePrice = prices[chosenTariff]
        const baseDiscount = discounts[chosenPeriod]
        const monthsCount = months[chosenPeriod]

        const price = basePrice * monthsCount * (1 - baseDiscount / 100) * 100
        return currencyFormatter.toShowFormatted(price | 0)
    }

    const calculateMonthlyPrice = () => {
        if (!open) return
        const basePrice = prices[chosenTariff]
        const baseDiscount = discounts[chosenPeriod]
        const price = basePrice * (1 - baseDiscount / 100) * 100
        return `${currencyFormatter.toShowFormatted(price)} / Мес.`
    }

    const initInvoiceCharge = async () => {
        try {
            setIsLoading(true)
            const createOrderResult = await createServiceOrder({
                type: 'Invoice',
                tariff: chosenTariff,
                period: chosenPeriod
            })

            setIsLoading(false)

            if (!createOrderResult.err) {
                const order = createOrderResult.serviceOrder as InvoiceServiceOrder
                window.open(order.invoiceLink)
            } else {
                generalStore.showSnackbar({
                    type: SnackbarType.error,
                    title: createOrderResult.message[0] || 'Не удалось создать заказ'
                })
            }
        } catch (e) {
            console.log('exception order creation')
            generalStore.showSnackbar({
                type: SnackbarType.error,
                title: 'Не удалось создать счет'
            })
            setIsLoading(false)
        }
    }

    const initCardCharge = async () => {
        if (!isServiceLegalDetailsLoaded) return

        const createOrderResult = await createServiceOrder({
            type: 'Card',
            tariff: chosenTariff,
            period: chosenPeriod
        })

        const serviceOrder = createOrderResult.serviceOrder as CardServiceOrder

        const options = {
            language: "ru-RU"
        }
        onClose()
        console.log(serviceLegalDetails.cloudPaymentsCheckout.publicId)
        //@ts-ignore
        const widget = new cp.CloudPayments(options);
        widget.pay('auth', // или 'auth'
            { //options
                publicId: serviceLegalDetails.cloudPaymentsCheckout.publicId,
                description: `Заказ №S${serviceOrder.orderId}`,
                amount: serviceOrder.price.price / 100, //сумма
                currency: 'RUB', //валюта
                invoiceId: `S${serviceOrder.orderId}`, //номер заказа  (необязательно)
                // accountId: `${}`, //идентификатор плательщика (необязательно)
                skin: "mini", //дизайн виджета (необязательно)
            },
            {
                // onSuccess: params.onSuccess,
                onSuccess: () => {
                    generalStore.showSnackbar({
                        type: SnackbarType.success,
                        title: 'Оплата прошла успешно'
                    })
                    onClose()
                },
                // onFail: params.onFail,
                onFail: () => {
                    console.log('error')
                    // generalStore.showSnackbar({
                    //     type: SnackbarType.error,
                    //     title: 'Не удалось провести оплату'
                    // })
                },
                onComplete: function (paymentResult: any, options: any) { //Вызывается как только виджет получает от api.cloudpayments ответ с результатом транзакции.
                    setTimeout(() => {
                        documentsStore.getOrganisationBranch()
                    }, 2000)
                    // setTimeout(() => {
                    //     rootStore.checkoutStore.reloadOrder();
                    //     rootStore.bookingCheckoutStore.reloadOrder()
                    // }, 2000);
                    //например вызов вашей аналитики Facebook Pixel
                    console.log(options, paymentResult);
                }
            }
        )
    }

    useEffect(() => {
        setChosenTariff(tariff)
    }, [tariff])

    useEffect(() => {
        setChosenPeriod('Year')
    }, [open])

    const closeForm = () => {
        onClose()
    }

    return (
        <Dialog
            open={open}
            onClose={closeForm}
            aria-labelledby="form-dialog-title"
            fullWidth={true}
            maxWidth={"sm"}
            fullScreen={!isSm}
        >
            <DialogTitle id="form-dialog-title">Выберите способ оплаты</DialogTitle>
            <DialogContent>
                <Box css={{display: 'flex', flexDirection: 'column', gridGap: 20}}>
                    <FormControl variant={"outlined"} fullWidth>
                        <InputLabel>
                            Период
                        </InputLabel>
                        <Select
                            value={chosenPeriod}
                            onChange={changeEventHandlerDecorator(setChosenPeriod)}
                            input={<OutlinedInput labelWidth={100}/>}
                        >
                            <MenuItem value={"Month"}>
                                Ежемесячно
                            </MenuItem>
                            <MenuItem value={"HalfYear"}>
                                6 месяцев со скидкой 10%
                            </MenuItem>
                            <MenuItem value={"Year"}>
                                12 месяцев со скидкой 25%
                            </MenuItem>
                        </Select>
                    </FormControl>

                    <FormControl variant={"outlined"} fullWidth>
                        <InputLabel>
                            Тариф
                        </InputLabel>
                        <Select
                            value={chosenTariff}
                            onChange={changeEventHandlerDecorator(setChosenTariff)}
                            input={<OutlinedInput labelWidth={100}/>}
                        >
                            <MenuItem value={"Simple"}>
                                Простой
                            </MenuItem>
                            <MenuItem value={"Basic"}>
                                Базовый
                            </MenuItem>
                            <MenuItem value={"Advanced"}>
                                Расширенный
                            </MenuItem>
                        </Select>
                    </FormControl>

                    <Box css={{display: 'flex', flexDirection: 'row', gridGap: 20}}>
                        <Typography
                            style={{
                                backgroundColor: 'rgba(60, 61, 74, 0.1)',
                                width: '100%', padding: 15,
                                borderRadius: 5,
                                border: '1px solid rgba(60, 61, 74, 0.3)'
                            }}
                        >
                            В месяц: {calculateMonthlyPrice()}
                        </Typography>

                        <Typography style={{
                            borderBottom: '1px solid rgba(60, 61, 74, 0.3)',
                            width: '100%',
                            padding: 15,
                            alignItems: 'bottom'
                        }} align={"center"}>
                            Итог к оплате: {calculatePrice()}
                        </Typography>
                    </Box>

                    <Box css={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                        <Checkbox
                            checked={isDocsChecked}
                            onChange={() => setIsDocsChecked(!isDocsChecked)}
                            value={isDocsChecked}
                            style={{ paddingLeft: 0 }}
                        />
                        <Typography>Я согласен с <a href='https://restify.one/documents/oferta' target="_blank">офертой</a> сервиса restify.one</Typography>
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button variant={"contained"} color={"primary"} disabled={isLoading || !isDocsChecked} onClick={() => initCardCharge()}>
                    Оплатить картой
                </Button>
                <Button variant={"contained"} color={"default"} disabled={isLoading || !isDocsChecked} onClick={() => initInvoiceCharge()}>
                    Скачать счет
                </Button>
                <Button onClick={closeForm} color="primary">
                    Закрыть
                </Button>
            </DialogActions>
        </Dialog>
    )
}